



























































































import {
	computed,
	defineComponent,
	onMounted,
	PropType,
	ref,
	watch,
} from '@vue/composition-api';
import deepEqual from 'deep-equal';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import InviteeCard from '@front-app/modules/group/components/admin/InviteeCard.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import RadioList from '@front-app/components/inputs/RadioList.vue';
import SearchBox from '@front-app/components/search/SearchBox.vue';
import {
	InviteeStatus,
	SortParameter,
	InviteeCounts,
} from '@front-app/services/GroupInvitationCampaignApi/model';
import { SortDirection } from '@front-app/services/SharedModels';
import { InvitationCampaignInviteesStore } from '@front-app/store/singletons/InvitationCampaignInvitees';
import { getInvitationCampaignInviteesSearchStore } from '@front-app/store/modules/invitationCampaignInviteeSearch';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { Size } from '@front-app/components/constants';
import { DropdownPosition } from '@front-app/components/dropdowns/dropdownPosition';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { AllButtonOption, InviteeButtonOption } from './inviteesListModal';
import InviteeBadge from './InviteeBadge.vue';

const LABELS_BY_FILTERS = {
	[AllButtonOption.ALL]: 'invitation-campaign.all-invitees',
	[InviteeStatus.ACTIVE]: 'invitation-campaign.active-invitees',
	[InviteeStatus.JOINED]: 'invitation-campaign.joined-invitees',
	[InviteeStatus.DECLINED]: 'invitation-campaign.declined-invitees',
	[InviteeStatus.PAUSED]: 'invitation-campaign.paused-invitees',
};

const INVITEE_STATUS_BUTTONS = [
	AllButtonOption.ALL,
	InviteeStatus.ACTIVE,
	InviteeStatus.JOINED,
	InviteeStatus.DECLINED,
	InviteeStatus.PAUSED,
];

export default defineComponent({
	components: {
		CtaButton,
		ErrorCard,
		InviteeCard,
		LoaderState,
		SearchBox,
		RadioList,
		InviteeBadge,
	},
	props: {
		statusFilter: {
			type: String as PropType<InviteeButtonOption>,
			required: true,
		},
		groupId: {
			type: String,
			required: true,
		},
		campaignId: {
			type: String,
			required: true,
		},
		counts: {
			type: Object as PropType<InviteeCounts>,
			required: true,
		},
	},
	setup(props, ctx) {
		const isDropdownOpen = ref(false);
		const searchEmail = ref('');
		const selectedStatusFilter = ref(props.statusFilter);

		// Invitee status dropdown
		const inviteeStatuses = computed(() => INVITEE_STATUS_BUTTONS.map((key) => ({
			key,
			id: key,
			label: ctx.root.$t(LABELS_BY_FILTERS[key]) as unknown as string,
			count: props.counts[key as keyof InviteeCounts],
		})));
		const selectedStatusLabel = computed(() => LABELS_BY_FILTERS[selectedStatusFilter.value]);

		const updateSelectedStatus = (inviteeStatus: InviteeButtonOption) => {
			selectedStatusFilter.value = inviteeStatus;
			isDropdownOpen.value = !isDropdownOpen.value;
		};
		const selectedStatuses = computed(() => {
			if (selectedStatusFilter.value === AllButtonOption.ALL) {
				return [InviteeStatus.ACTIVE, InviteeStatus.DECLINED, InviteeStatus.JOINED, InviteeStatus.PAUSED];
			}
			return [selectedStatusFilter.value];
		});
		// END Invitee status dropdown

		// Paginated invitees search
		const paginatedInvitees = getInvitationCampaignInviteesSearchStore(InvitationCampaignInviteesStore, computed(() => ({
			groupId: props.groupId,
			campaignId: props.campaignId,
			search: searchEmail.value,
			status: selectedStatuses.value,
			orderBy: SortParameter.EMAIL,
			sortDirection: SortDirection.DESC,
		})));
		// End Pagination

		// Search
		const resetPaginationWithLoader = () => paginatedInvitees.reset({ clearBeforeFetch: true });
		watch(() => ({ email: searchEmail.value, statuses: selectedStatuses.value }), (newVal, oldVal) => {
			if (deepEqual(newVal, oldVal)) {
				return;
			}

			if (!deepEqual(newVal.statuses, oldVal.statuses) || newVal.email === '') {
				// If the statuses have changed, we do clear, then fetch, to trigger the loading spinner
				paginatedInvitees.reset({ clearBeforeFetch: true });
			} else {
				// If only the email search has changed, we can load it in the background for a nicer effect
				paginatedInvitees.reset({ clearAfterFetch: true });
			}
		});
		onMounted(resetPaginationWithLoader);
		// END Search

		// Pause or resume all invitees
		const activeCount = computed(() => InvitationCampaignInviteesStore.getCountForStatus(InviteeStatus.ACTIVE));
		const pausedCount = computed(() => InvitationCampaignInviteesStore.getCountForStatus(InviteeStatus.PAUSED));

		const isPauseOrResumeButtonDisplayed = computed(() => activeCount.value || pausedCount.value);
		const inviteeStatusToApply = computed(() => {
			if (activeCount.value > pausedCount.value) {
				return InviteeStatus.PAUSED;
			}
			return InviteeStatus.ACTIVE;
		});

		const {
			track,
			isError: isErrorPausingOrResumingInvitees,
			isLoading: isPausingOrResumingInvitees,
			isSuccess: isSuccessPausingOrResumingInvitees,
		} = useAsyncStatus();

		const pauseOrResumeInvitees = async () => {
			await track(InvitationCampaignInviteesStore.pauseOrResumeInviteeStatuses(
				props.groupId,
				props.campaignId,
				inviteeStatusToApply.value,
			));

			if (isSuccessPausingOrResumingInvitees.value) {
				await InvitationCampaignInviteesStore.fetchInviteeCounts(
					props.groupId,
					props.campaignId,
				);
				paginatedInvitees.reset({ clearAfterFetch: true });
			}
		};
		// END Pause or resume all invitees

		return {
			// Fetching/paginating invitees
			loadMoreInvitees: paginatedInvitees.loadMore,
			resetPagination: resetPaginationWithLoader,
			isFetchingInvitees: paginatedInvitees.isFetchingInvitees,
			isLoadingCurrentPage: paginatedInvitees.isLoadingCurrentPage,
			inviteesToRender: paginatedInvitees.inviteesToRender,
			hasNextPage: paginatedInvitees.hasNextPage,
			// Pause or resume all invitees
			isPauseOrResumeButtonDisplayed,
			InviteeStatus,
			inviteeStatusToApply,
			pauseOrResumeInvitees,
			isErrorPausingOrResumingInvitees,
			isPausingOrResumingInvitees,
			// Invitee status dropdown
			isDropdownOpen,
			DropdownPosition,
			AllButtonOption,
			selectedStatusFilter,
			selectedStatusLabel,
			inviteeStatuses,
			updateSelectedStatus,
			// Search
			searchEmail,
			// Util
			ButtonPalettes,
			Size,
		};
	},
});
